import React, { useRef, useCallback, useState } from 'react'
import {
  Box,
  IconButton,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
} from '@mui/material'
import {
  AddPhotoAlternate as AddPhotoIcon,
  CameraAlt as CameraIcon,
  Close as CloseIcon,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import { Screenshot, DEFAULT_SCREENSHOT_CONFIG } from '../types/screenshot.types'
import { useScreenshots } from '../hooks/useScreenshots'

interface ScreenshotUploaderProps {
  screenshots: Screenshot[]
  onScreenshotsChange: (screenshots: Screenshot[]) => void
  maxScreenshots?: number
  disabled?: boolean
}

export function ScreenshotUploader({
  screenshots,
  onScreenshotsChange,
  maxScreenshots = DEFAULT_SCREENSHOT_CONFIG.maxScreenshots,
  disabled = false,
}: ScreenshotUploaderProps) {
  const { t } = useTranslation('core')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { isCapturing, error, addFromCapture, addFromFile, removeScreenshot, canCapture } =
    useScreenshots({
      screenshots,
      onChange: onScreenshotsChange,
      maxScreenshots,
    })

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (file) {
        await addFromFile(file)
        // Reset input so same file can be selected again
        event.target.value = ''
      }
    },
    [addFromFile]
  )

  const handleCapture = useCallback(async () => {
    await addFromCapture()
  }, [addFromCapture])

  const handleRemove = useCallback(
    (id: string) => {
      removeScreenshot(id)
    },
    [removeScreenshot]
  )

  const [previewScreenshot, setPreviewScreenshot] = useState<Screenshot | null>(null)

  const canAddMore = screenshots.length < maxScreenshots

  const ghostButtonSx = {
    color: 'text.secondary',
    textTransform: 'none',
    fontSize: '0.8125rem',
    fontWeight: 400,
    px: 1.5,
    py: 0.75,
    minWidth: 'auto',
    '&:hover': {
      bgcolor: 'grey.100',
    },
    '& .MuiButton-startIcon': {
      marginRight: 0.75,
    },
  }

  return (
    <Box>
      {/* Screenshot previews */}
      {screenshots.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            mb: 2,
          }}
        >
          {screenshots.map((screenshot) => (
            <Box
              key={screenshot.id}
              sx={{
                position: 'relative',
                width: 80,
                height: 80,
                borderRadius: 1,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'grey.300',
              }}
            >
              <img
                src={screenshot.previewUrl}
                alt={screenshot.filename}
                onClick={() => setPreviewScreenshot(screenshot)}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  cursor: 'pointer',
                }}
              />
              <IconButton
                size="small"
                onClick={() => handleRemove(screenshot.id)}
                disabled={disabled}
                sx={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  bgcolor: 'rgba(0, 0, 0, 0.5)',
                  color: 'white',
                  padding: 0.25,
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.7)',
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: '0.875rem' }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {/* Action buttons */}
      {canAddMore && (
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, alignItems: 'center' }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            disabled={disabled || isCapturing}
          />

          {canCapture && (
            <Button
              variant="text"
              startIcon={
                isCapturing ? <CircularProgress size={16} /> : <CameraIcon fontSize="small" />
              }
              onClick={handleCapture}
              disabled={disabled || isCapturing}
              sx={ghostButtonSx}
              data-testid="screenshot-capture-button"
            >
              {t('supportAssistant.screenshot.addCurrentPage')}
            </Button>
          )}

          <Button
            variant="text"
            startIcon={<AddPhotoIcon fontSize="small" />}
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || isCapturing}
            sx={ghostButtonSx}
            data-testid="screenshot-upload-button"
          >
            {t('supportAssistant.screenshot.uploadImage')}
          </Button>
        </Box>
      )}

      {/* Error message */}
      {error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
          {error}
        </Typography>
      )}

      {/* Full-page image preview modal */}
      <Dialog
        open={!!previewScreenshot}
        onClose={() => setPreviewScreenshot(null)}
        maxWidth={false}
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
            m: 1,
            maxHeight: '95vh',
            maxWidth: '95vw',
          },
        }}
        slotProps={{
          backdrop: {
            sx: { bgcolor: 'rgba(0, 0, 0, 0.85)' },
          },
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <IconButton
            onClick={() => setPreviewScreenshot(null)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              zIndex: 1,
              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
            }}
          >
            <CloseIcon />
          </IconButton>
          {previewScreenshot && (
            <img
              src={previewScreenshot.previewUrl}
              alt={previewScreenshot.filename}
              style={{
                maxWidth: '100%',
                maxHeight: '90vh',
                objectFit: 'contain',
                borderRadius: 4,
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  )
}
